import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, X, Gauge, Search, MessageCircle, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BtnPrimary, BtnGhost } from "@/components/Buttons";
import { BrowserMockup, Chip } from "@/components/BrowserMockup";
import { Marquee } from "@/components/Marquee";
import { SECONDARY_SERVICES, MAIN_SERVICE, COMPARISON, PROCESS, VALUES, FEATURES, IMAGES } from "@/lib/data";

const Section = ({ children, className = "", ...rest }) => (
  <section className={`mx-auto max-w-[1440px] px-6 md:px-12 ${className}`} {...rest}>{children}</section>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const lines = [
  <React.Fragment key="get">
    <span className="text-grad">Get</span>{" "}
    <span>your</span>
  </React.Fragment>,
  "business",
  ];
  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="pointer-events-none absolute right-[-10%] top-10 h-[520px] w-[520px] rounded-full bg-grad opacity-[0.22] blur-[130px]" />
      <div className="pointer-events-none absolute left-[-8%] top-64 h-[400px] w-[400px] rounded-full bg-lightblue opacity-30 blur-[130px]" />

      <Section className="grid items-center gap-8 lg:grid-cols-[0.90fr_1.10fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="ring-grad mb-7 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-medium text-muted-foreground"
          >
            <span className="h-2 w-2 rounded-full bg-grad" /> Estúdio de Web Design · Portugal
          </motion.div>

          <h1 className="font-display text-[16vw] font-extrabold leading-[0.9] tracking-[-0.03em] text-ink sm:text-[11vw] lg:text-[6.6rem]">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <span className="line-reveal block pb-[0.06em]" style={{ animationDelay: `${0.15 + i * 0.12}s` }}>{l}</span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <span className="line-reveal block pb-[0.14em] text-grad" style={{ animationDelay: "0.39s" }}>visible.</span>
            </span>
          </h1>

          <Reveal delay={2} className="mt-7 max-w-lg">
            <p className="text-lg leading-relaxed text-muted-foreground">
              Um website moderno faz o seu negócio parecer maior, mais profissional e mais fiável.
              Nós criamos esse website — bonito, rápido e feito à sua medida — para atrair mais
              contactos e mais clientes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <BtnPrimary to="/contacto" data-testid="hero-contact-btn">Fale connosco</BtnPrimary>
              <BtnGhost to="/funcionalidades" data-testid="hero-features-btn">Ver funcionalidades</BtnGhost>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["Sem templates", "Feito à medida", "Rápido e responsivo"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-regatta" /> {t}</span>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="md:hidden">
           <BrowserMockup
             src={IMAGES.heroUI}
             mobileSrc={IMAGES.heroUIMobile}
             alt="Exemplo de um website moderno criado pela Get Visible"
             loading="eager"
             fetchPriority="high"
           />
          </div>

          <motion.div
           style={{ y: y1 }}
           className="hidden md:block"
          >
           <BrowserMockup
             src={IMAGES.heroUI}
             alt="Exemplo de um website moderno criado pela Get Visible"
             loading="eager"
             fetchPriority="high"
           />
          </motion.div>

          <motion.div style={{ y: y2 }} className="absolute -bottom-8 -left-4 hidden w-40 overflow-hidden rounded-[1.4rem] border-4 border-white bg-white shadow-[0_30px_60px_-20px_rgba(23,21,15,0.4)] sm:block">
            <img src={IMAGES.phoneHand} alt="Website da Get Visible visto no telemóvel" loading="lazy" className="aspect-[9/16] w-full object-cover" />
          </motion.div>

          <div className="animate-float absolute -right-3 top-10 hidden md:block">
            <Chip icon={Gauge} label="Marketing eficaz" />
          </div>
          <div className="animate-float2 absolute -left-6 top-1/2 hidden md:block">
            <Chip icon={Search} label="Mais clientes" />
          </div>
          <div className="animate-float absolute -bottom-4 right-8 hidden md:block" style={{ animationDelay: "1.2s" }}>
            <Chip icon={MessageCircle} label="Design Moderno" />
          </div>
        </motion.div>
      </Section>
    </section>
  );
};

const MainService = () => (
  <Section className="py-24 md:py-32">
    <div className="grid gap-12 rounded-[2rem] border border-line bg-tint p-8 md:grid-cols-2 md:items-center md:gap-16 md:p-14">
      <Reveal>
        <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">O que fazemos melhor</span>
        <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl text-balance">
          {MAIN_SERVICE.title}
        </h2>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">{MAIN_SERVICE.body}</p>
        <Link to="/servicos" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-regatta transition-colors hover:text-ink">
          Ver todos os serviços <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
      <Reveal delay={1} className="flex flex-col gap-3">
        {MAIN_SERVICE.points.map((p, i) => (
          <div key={p} className="ring-grad flex items-center gap-4 rounded-2xl bg-white p-5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-grad text-white text-sm font-semibold">{i + 1}</span>
            <span className="font-medium text-ink">{p}</span>
          </div>
        ))}
      </Reveal>
    </div>
  </Section>
);

const SecondaryServices = () => (
  <Section className="pb-8">
    <div className="mb-12 flex flex-col gap-6 border-t border-line pt-10 md:flex-row md:items-end md:justify-between">
      <div>
        <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">Também fazemos</span>
        <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">Branding e SEO, se precisar</h2>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {SECONDARY_SERVICES.map((s, i) => {
        const Icon = s.icon;
        return (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group flex gap-5 rounded-2xl border border-line bg-white p-7 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-transparent hover:shadow-[0_24px_50px_-24px_rgba(23,21,15,0.25)]"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-line bg-tint text-ink transition-colors duration-300 group-hover:border-transparent group-hover:bg-grad group-hover:text-white">
              <Icon className="h-5 w-5" strokeWidth={1.6} />
            </span>
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

const Comparison = () => (
  <Section className="py-24 md:py-32">
    <Reveal className="mx-auto mb-14 max-w-2xl text-center">
      <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">A diferença</span>
      <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">Não é só mais bonito. É melhor.</h2>
      <p className="mt-4 text-muted-foreground">A diferença entre um website antigo e um website bem feito nota-se — e os seus clientes também a notam.</p>
    </Reveal>

    <div className="grid gap-6 md:grid-cols-2">
      <Reveal className="rounded-[1.6rem] border border-line bg-tint p-8 md:p-10">
        <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{COMPARISON.old.label}</p>
        <ul className="mt-6 space-y-4">
          {COMPARISON.old.items.map((it) => (
            <li key={it} className="flex items-center gap-3 text-muted-foreground">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink/5 text-ink/40"><X className="h-3.5 w-3.5" /></span>
              <span className="line-through decoration-ink/20">{it}</span>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={1} className="ring-grad relative overflow-hidden rounded-[1.6rem] bg-ink p-8 text-white md:p-10">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-grad opacity-40 blur-3xl" />
        <p className="relative text-sm font-medium uppercase tracking-wider text-white/60">{COMPARISON.neu.label}</p>
        <ul className="relative mt-6 space-y-4">
          {COMPARISON.neu.items.map((it) => (
            <li key={it} className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-grad text-white"><Check className="h-3.5 w-3.5" /></span>
              <span className="font-medium">{it}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </Section>
);

const Philosophy = () => (
  <Section className="pb-24 md:pb-32">
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
      <Reveal>
        <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">Como pensamos</span>
        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl text-balance">
          Cada negócio é diferente. O seu website também deve ser.
        </h2>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
          Nunca usamos a mesma solução para todos. Adaptamos tudo — cores, tipografia, estrutura e
          funcionalidades — à sua marca. E não ficamos à espera: sugerimos ideias e melhorias como se o
          projeto fosse nosso.
        </p>
      </Reveal>
      <div className="flex flex-col gap-4">
        {VALUES.map((v, i) => {
          const Icon = v.icon;
          return (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.55, delay: i * 0.08 }}
              className="flex gap-5 rounded-2xl border border-line bg-white p-6"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-grad text-white">
                <Icon className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div>
                <h3 className="font-display text-xl font-semibold text-ink">{v.title}</h3>
                <p className="mt-2 leading-relaxed text-muted-foreground">{v.body}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </Section>
);

const FeaturesPreview = () => (
  <div className="bg-tint">
    <Section className="py-24 md:py-32">
      <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">Possibilidades</span>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">O que o seu website pode incluir</h2>
        </div>
        <Link to="/funcionalidades" className="link-underline inline-flex items-center gap-2 self-start text-sm text-ink md:self-auto">
          Ver todas as funcionalidades <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
              className="group flex items-center gap-3 rounded-xl border border-line bg-white px-4 py-4 transition-colors duration-300 hover:border-transparent hover:shadow-[0_18px_40px_-24px_rgba(23,21,15,0.3)]"
            >
              <Icon className="h-5 w-5 shrink-0 text-regatta transition-colors group-hover:text-brightblue" strokeWidth={1.7} />
              <span className="text-sm font-medium text-ink">{f.title}</span>
            </motion.div>
          );
        })}
      </div>
    </Section>
  </div>
);

const Process = () => (
  <Section className="py-24 md:py-32">
    <Reveal className="mb-14 max-w-2xl">
      <span className="text-xs font-medium uppercase tracking-[0.25em] text-grad">Venha perceber</span>
      <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">O nosso processo de trabalho</h2>
      <p className="mt-4 text-muted-foreground">Do primeiro "olá" ao lançamento, está sempre a par de tudo. Sem surpresas, sem complicações. Simples, humano e acompanhado.</p>
    </Reveal>
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {PROCESS.map((p, i) => (
        <motion.div
          key={p.n}
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: i * 0.08 }}
          className="relative rounded-2xl border border-line bg-white p-7"
        >
          <span className="font-display text-5xl font-extrabold text-grad">{p.n}</span>
          <h3 className="mt-5 font-display text-xl font-semibold text-ink">{p.t}</h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const PricingCTA = () => (
  <Section className="pb-24 md:pb-32">
    <div className="noise relative overflow-hidden rounded-[2rem] bg-ink p-10 text-white md:p-16">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-grad opacity-30 blur-[100px]" />
      <div className="relative grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
        <div>
          <span className="text-xs font-medium uppercase tracking-[0.25em] text-white/50">Orçamento</span>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl text-balance">
            Cada projeto é diferente. O orçamento também.
          </h2>
          <p className="mt-5 max-w-lg leading-relaxed text-white/70">
            O valor depende da dimensão, dos objetivos e da complexidade do seu website. Garantimos
            sempre total transparência desde o primeiro contacto — sem letras pequenas.
          </p>
        </div>
        <div className="flex flex-col gap-4 lg:items-end">
          <BtnPrimary to="/contacto" data-testid="pricing-cta">Diga-nos o seu budget</BtnPrimary>
          <Link to="/servicos" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
            Ver o que fazemos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  </Section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <div className="mt-16 md:mt-24"><Marquee /></div>
      <MainService />
      <SecondaryServices />
      <Comparison />
      <Philosophy />
      <FeaturesPreview />
      <Process />
      <PricingCTA />
    </>
  );
}
